/* eslint-disable max-lines */
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
import {
    ActorRef,
    ActorRefFrom, assertEvent, assign, fromPromise, sendTo, setup,
} from 'xstate';
import { produce } from 'immer';
import { getBFFApiClient } from '@acp/feature/auth';
import HttpStatusCodes from 'http-status-codes';
import {
    APIResponse, PropertyDocumentUtil, TemporaryDocumentUploadResponse,
} from '@acp/shared';

type DocumentMachineEvents = { type: 'START_UPLOAD'; file: File }
| { type: 'UPLOAD_COMPLETE'; id: string; uploadedFileReference: string; fileName: string; fileSize: number }
| { type: 'UPLOAD_FAILED'; id: string }
| { type: 'CLEAR_COMPLETED' }
| { type: 'REMOVE_FILE_UPLOAD'; id: string; uploadedFileReference: string | null };

type FileUploadMachineEvents = { type: 'CANCEL' }
| { type: 'SET_UPLOAD_PERCENTAGE'; percentage: number }
| { type: 'RETRY' }
| { type: 'REMOVE' }
| { type: 'START' };

const uploadFileActor = fromPromise(
    async (
        {
            input,
        }: {
            input: {
                parent: ActorRef<any, FileUploadMachineEvents>;
                uploadDestination: string;
                file: File;
                abortController: AbortController;
            };
        }
    ) => {
        const form = new FormData();
        form.append('file', input.file);

        const response = await getBFFApiClient()
            // TODO: When writing the API that these files will upload to
            // DO NOT send back direct S3 references and they could be
            // tampered with, allowing any abritrary S3 document to be used
            // Consider a hashing algo / some other form of protection (BFF generated JWT)
            // to prevent tampering
            .post<APIResponse<{ fileReference: string } | TemporaryDocumentUploadResponse>>(
            input.uploadDestination,
            form,
            {
                onUploadProgress: (progressEvent) => {
                    const percentage = progressEvent.progress;
                    if (percentage === undefined) return;

                    input.parent.send({
                        type: 'SET_UPLOAD_PERCENTAGE',
                        percentage: Math.round(percentage * 100),
                    });
                },
                signal: input.abortController.signal,
            }
        );

        if (response.status !== HttpStatusCodes.OK || !response.data.success) {
            throw new Error(response.data.error ?? 'Unknown error');
        }

        return response.data.data;
    }
);

export const documentUploadMachine = setup({
    types: {
        context: {} as {
            uploadDestination: string;
            abortController: AbortController;
            file: File | null;
            uploadPercentage: number;
            parentRef: ActorRef<any, DocumentMachineEvents>;
            error: string | null;
            maxFileSizeBytes: number;
            uploadedFileReference: string | null;
            fileName: string;
        },
        input: {} as {
            file: File | null;
            parentRef: ActorRef<any, DocumentMachineEvents>;
            maxFileSizeBytes: number;
            uploadDestination: string;
            fileName?: string;
            uploadedFileReference?: string;
        },
        events: {} as FileUploadMachineEvents,
    },
    actors: {
        uploadFile: uploadFileActor,
    },
    actions: {
        abortUpload: ({ context }) => {
            context.abortController.abort();
        },
        // eslint-disable-next-line max-len
        removeFileUploadMachine: sendTo(({ context }) => context.parentRef, ({ self, context }) => ({
            type: 'REMOVE_FILE_UPLOAD',
            id: self.id,
            uploadedFileReference: context.uploadedFileReference,
        })),
        notifySuccess: sendTo(({ context }) => context.parentRef, ({ self, context }) => ({
            type: 'UPLOAD_COMPLETE',
            id: self.id,
            uploadedFileReference: context.uploadedFileReference!,
            fileName: context.file!.name,
            fileSize: context.file!.size,
        })),
        notifyFailed: sendTo(({ context }) => context.parentRef, ({ self }) => ({
            type: 'UPLOAD_FAILED',
            id: self.id,
        })),
    },
    guards: {
        isInvalidFileType: ({ context }) => !PropertyDocumentUtil.isValid(
            context.file!.name,
            context.file!.type
        ),
        // eslint-disable-next-line max-len
        hasInvalidFilenameCharacters: ({ context }) => !PropertyDocumentUtil.isValidFileName(
            context.file!.name
        ),
        isInvalidFileSize: ({ context }) => context.file!.size > context.maxFileSizeBytes,
        hasFile: ({ context }) => context.file !== null,
        hasNoFile: ({ context }) => context.file === null,
    },
}).createMachine({
    initial: 'queued',
    context: ({ input }) => ({
        abortController: new AbortController(),
        file: input.file,
        maxFileSizeBytes: input.maxFileSizeBytes,
        uploadPercentage: 0,
        parentRef: input.parentRef,
        uploadDestination: input.uploadDestination,
        error: null,
        uploadedFileReference: input.uploadedFileReference ?? null,
        fileName: input.file ? input.file.name : input.fileName ?? '',
    }),
    states: {
        queued: {
            on: {
                START: {
                    target: 'uploading',
                },
                CANCEL: {
                    target: 'completed',
                    actions: 'abortUpload',
                },
            },
            always: [
                {
                    guard: 'hasNoFile',
                    target: 'success',
                },
                {
                    guard: 'isInvalidFileSize',
                    target: 'failed',
                    actions: assign(({ context }) => produce(context, (draft) => {
                        draft.error = 'File size is too large';
                    })),
                },
                {
                    guard: 'isInvalidFileType',
                    target: 'failed',
                    actions: assign(({ context }) => produce(context, (draft) => {
                        draft.error = 'Invalid file type';
                    })),
                },
                {
                    guard: 'hasInvalidFilenameCharacters',
                    target: 'failed',
                    actions: assign(({ context }) => produce(context, (draft) => {
                        draft.error = 'File name cannot contain special characters (\\ / : * ? " < > | # % ~ `)';
                    })),
                },

            ],
        },
        uploading: {
            invoke: {

                src: 'uploadFile',
                input: ({ self, context }) => ({
                    file: context.file!,
                    parent: self,
                    abortController: context.abortController,
                    uploadDestination: context.uploadDestination,
                }),
                onDone: {
                    target: 'success',
                    actions: [
                        assign(({ context, event }) => produce(context, (draft) => {
                            if ('id' in event.output) {
                                draft.uploadedFileReference = event.output.id;
                            } else {
                                draft.uploadedFileReference = event.output.fileReference;
                            }
                        })),
                        { type: 'notifySuccess' },
                    ],
                },
                onError: {
                    target: 'failed',
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        draft.error = (event.error as Error).message;
                    })),
                },
            },
            on: {
                CANCEL: {
                    target: 'completed',
                    actions: 'abortUpload',
                },
                SET_UPLOAD_PERCENTAGE: {
                    actions: assign(({ context, event }) => produce(context, (draft) => {
                        assertEvent(event, 'SET_UPLOAD_PERCENTAGE');
                        draft.uploadPercentage = event.percentage;
                    })),
                },
            },
        },
        success: {
            on: {
                CANCEL: 'completed',
            },
        },
        failed: {
            entry: 'notifyFailed',
            on: {
                CANCEL: 'completed',
            },
        },
        completed: {
            entry: 'removeFileUploadMachine',
            type: 'final',
        },
    },
});

const genericUploadQueueMachine = setup({
    types: {
        events: {} as DocumentMachineEvents,
        context: {} as {
            queue: ActorRefFrom<typeof documentUploadMachine>[];
            uploadCapacity: number;
            maxFileSizeBytes: number;
            uploadDestination: string;
        },
        input: {} as {
            uploadCapacity: number;
            maxFileSizeBytes: number;
            uploadDestination: string;
            defaultFiles: { fileName: string; uploadedFileReference: string }[];
        },
    },
    actors: {
        fileUpload: documentUploadMachine,
    },
    actions: {
        processQueue: ({ context }) => {
            // Simple queue mechanism that will ensure
            // that only a certain number of uploads (contex.uploadCapacity)
            // are running at the same time.
            const hasQueuedUploaders = context.queue.some(
                (uploader) => uploader.getSnapshot().matches('queued')
            );

            if (!hasQueuedUploaders) return;

            const runningUploaders = context.queue.filter(
                (uploader) => uploader.getSnapshot().matches('uploading')
            );

            if (runningUploaders.length >= context.uploadCapacity) return;

            const availableCapacity = context.uploadCapacity - runningUploaders.length;

            if (availableCapacity <= 0) return;

            let usedCapacity = 0;

            for (const uploader of context.queue) {
                if (uploader.getSnapshot().value === 'queued') {
                    uploader.send({ type: 'START' });

                    usedCapacity += 1;
                }

                if (usedCapacity >= availableCapacity) break;
            }
        },
        pushFileToQueue: assign(({
            context, event, spawn, self,
        }) => produce(context, (draft) => {
            assertEvent(event, 'START_UPLOAD');
            draft.queue.push(
                spawn('fileUpload', {
                    id: `file-${window.crypto.randomUUID()}`,
                    input: {
                        file: event.file,
                        uploadDestination: context.uploadDestination,
                        parentRef: self,
                        maxFileSizeBytes: context.maxFileSizeBytes,
                    },
                })
            );
        })),
        uploadedCompleted: (_, _params: {
            uploadFileReference: string;
            uploadFileName: string;
            uploadFileSize: number;
        }) => {},
        uploadFailed: (_, _params: {
            uploadFileReference: string;
            uploadFileName: string;
            uploadFileSize: number;
        }) => {},
        uploadCleared: (_, _params: {
            id: string;
            uploadedFileReference: string | null;
        }) => {},
        removeFileUploadMachine: assign(
            ({ context }, { id }: { id: string }) => produce(context, (draft) => {
                draft.queue = context.queue.filter(
                    (upload) => upload.id !== id
                );
            })
        ),
        clearCompleted: assign(
            ({ context }) => produce(context, (draft) => {
                draft.queue = context.queue.filter(
                    (upload) => !upload.getSnapshot().matches('success')
                        && !upload.getSnapshot().matches('failed')
                );
            })
        ),
        queueLengthChanged: (_, _params: {
            activeCount: number;
        }) => {},
    },
}).createMachine({
    id: 'genericUploader',
    initial: 'idle',
    context: ({ input, spawn, self }) => {
        const queue = input.defaultFiles.map(
            (x) => spawn('fileUpload', {
                id: `file-${window.crypto.randomUUID()}`,
                input: {
                    file: null,
                    uploadDestination: input.uploadDestination,
                    parentRef: self,
                    maxFileSizeBytes: input.maxFileSizeBytes,
                    fileName: x.fileName,
                    uploadedFileReference: x.uploadedFileReference,
                },
            })
        );

        return {
            queue,
            uploadCapacity: input.uploadCapacity,
            maxFileSizeBytes: input.maxFileSizeBytes,
            uploadDestination: input.uploadDestination,
        };
    },
    states: {
        idle: {
            after: {
                1000: 'enqueue',
            },
        },
        enqueue: {
            entry: 'processQueue',
            after: {
                1000: 'idle',
            },
        },
    },
    on: {
        START_UPLOAD: {
            actions: [
                {
                    type: 'pushFileToQueue',
                },
                {
                    type: 'queueLengthChanged',
                    params: ({ context }) => ({
                        activeCount: context.queue.filter(
                            (uploader) => uploader.getSnapshot().matches('uploading')
                                    || uploader.getSnapshot().matches('queued')
                        ).length,
                    }),
                },
            ],
        },
        UPLOAD_COMPLETE: {
            actions: [
                {
                    type: 'uploadedCompleted',
                    params: ({ event }) => ({
                        uploadFileReference: event.uploadedFileReference,
                        uploadFileName: event.fileName,
                        uploadFileSize: event.fileSize,
                    }),
                },
                {
                    type: 'queueLengthChanged',
                    params: ({ context }) => ({
                        activeCount: context.queue.filter(
                            (uploader) => uploader.getSnapshot().matches('uploading')
                                    || uploader.getSnapshot().matches('queued')
                        ).length,
                    }),
                },
            ],
        },
        UPLOAD_FAILED: {
            actions: [
                {
                    type: 'uploadFailed',
                    params: ({ event }) => ({
                        uploadFileReference: event.id,
                        uploadFileName: 'Unknown',
                        uploadFileSize: 0,
                    }),
                },
                {
                    type: 'queueLengthChanged',
                    params: ({ context }) => ({
                        activeCount: context.queue.filter(
                            (upload) => upload.getSnapshot().matches('uploading')
                                    || upload.getSnapshot().matches('queued')
                        ).length,
                    }),
                },
            ],
        },
        CLEAR_COMPLETED: {
            actions: 'clearCompleted',
        },
        REMOVE_FILE_UPLOAD: {
            actions: [
                {
                    type: 'uploadCleared',
                    params: ({ event }) => ({
                        id: event.id,
                        uploadedFileReference: event.uploadedFileReference,
                    }),
                },
                {
                    type: 'removeFileUploadMachine',
                    params: ({ event }) => ({
                        id: event.id,
                    }),
                },
                {
                    type: 'queueLengthChanged',
                    params: ({ context }) => ({
                        activeCount: context.queue.filter(
                            (upload) => upload.getSnapshot().matches('uploading')
                                    || upload.getSnapshot().matches('queued')
                        ).length,
                    }),
                },
            ],
        },
    },
});

export { genericUploadQueueMachine };
